'use client';

import { useEffect, useId, useState } from 'react';
import clsx from 'classnames';
import { DOTS, usePagination } from './usePagination';
import { PaginationProgress } from './PaginationProgress';
import { useWindowDimensions } from 'ui';

import { PaginationProps } from './types';
import styles from './Pagination.module.scss';

export const Pagination = ({
	onPageChange,
	totalCount,
	currentPage,
	pageSize,
	showProgress = false,
	previousPageLabel = 'Poprzednia strona',
	nextPageLabel = 'Następna strona',
	progressLabel = ({ currentCount, totalCount }) =>
		`Widziałeś ${currentCount} z ${totalCount} produktów`,
}: PaginationProps) => {
	const id = useId();
	const [width, setWidth] = useState<number>(0);
	const { width: browserWidth } = useWindowDimensions();

	const paginationRange = usePagination({
		currentPage,
		totalCount,
		siblingCount: 1,
		pageSize,
	});

	useEffect(() => {
		setWidth(browserWidth);
	}, [browserWidth]);

	if (currentPage === 0 || paginationRange.length < 2) {
		return null;
	}

	const onNext = () => {
		onPageChange(currentPage + 1);
	};

	const onPrevious = () => {
		onPageChange(currentPage - 1);
	};

	let lastPage = paginationRange[paginationRange.length - 1];

	return (
		<>
			{showProgress && (
				<PaginationProgress
					progress={(currentPage / Number(lastPage)) * 100}
					label={progressLabel({
						currentCount: currentPage === Number(lastPage) ? totalCount : pageSize * currentPage,
						totalCount,
					})}
				/>
			)}
			<ul className={styles.paginationContainer}>
				{currentPage !== 1 ? (
					<li
						className={clsx(styles.paginationItem, {
							[styles['paginationItem--disabled']]: currentPage === 1,
						})}
						onClick={onPrevious}
					>
						{width > 768 ? (
							<div
								className={clsx(
									styles.paginationItem__arrow,
									styles['paginationItem__arrow--left']
								)}
							/>
						) : (
							previousPageLabel
						)}
					</li>
				) : null}
				{paginationRange.map((pageNumber) => {
					if (pageNumber === DOTS) {
						return (
							<li
								key={`dots-${id}`}
								className={clsx(styles.paginationItem, styles['paginationItem--dots'])}
							>
								. . .
							</li>
						);
					}

					return (
						<li
							key={pageNumber}
							className={clsx(styles.paginationItem, {
								[styles['paginationItem--selected']]: pageNumber === currentPage,
							})}
							onClick={pageNumber === currentPage ? undefined : () => onPageChange(+pageNumber)}
						>
							{pageNumber}
						</li>
					);
				})}
				{currentPage !== lastPage ? (
					<li
						className={clsx(styles.paginationItem, {
							[styles['paginationItem--disabled']]: currentPage === lastPage,
						})}
						onClick={onNext}
					>
						{nextPageLabel}
					</li>
				) : null}
			</ul>
		</>
	);
};
